// ✅ src/pages/OrderManagement/DailyProgressMonthlyWise.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaBuilding,
  FaDownload,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBars,
  FaHome,
  FaChartBar,
  FaUsers,
  FaFileAlt,
  FaTasks,
  FaWarehouse,
  FaSave,
  FaUndo,
  FaExpand,
  FaCompress,
  FaPrint,
  FaSearch,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API = "http://nissi-india.com/api/dprMonthWise";

/** ---------------- helpers ---------------- **/
const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const makeYears = () => {
  const end = new Date().getFullYear();
  const ys = [];
  for (let y = 2015; y <= end; y++) ys.push(y);
  return ys;
};

const emptyDprRow = () => {
  const r = { remarks: "" };
  for (let d = 1; d <= 31; d++) {
    r[`p${d}`] = "0";
    r[`a${d}`] = "0";
  }
  return r;
};

export default function ProgramDPRComponent() {
  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedView, setExpandedView] = useState(false);

  const location = useLocation();
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

 

  // ✅ localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS = getLS("userId") || getLS("loginuserid");

  // ✅ numeric userId for payload
  const userId = useMemo(() => toInt(userIdLS || 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branchtype": branchTypeLS,
      "x-userid": userIdLS,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userIdLS]
  );

  const contractId = useMemo(() => {
      const qs = new URLSearchParams(location.search);
      const cid = qs.get("contractid") || qs.get("contractId") || "";
      const n = Number(cid || 0);
      return Number.isFinite(n) ? n : 0;
    }, [location.search]);
  
    const withContractId = (path) => {
      if (!contractId) return path;
  
      const url = new URL(path, window.location.origin);
      url.searchParams.set("contractId", contractId);
      return url.pathname + url.search;
    };
  
    const sidebarItems = useMemo(
      () => [
        { name: "Date/LOA", icon: FaCalendarAlt, path: withContractId("/OrderManagement/ProjectEntry") },
        { name: "BOQ/Operative", icon: FaFileInvoiceDollar, path: withContractId("/OrderManagement/BOQ"), active: true },
        { name: "Program/DPR", icon: FaChartBar, path: withContractId("/OrderManagement/DailyProgressMonthlyWise") },
        { name: "Purchase Order", icon: FaFileAlt, path: withContractId("/OrderManagement/PurchaseOrderListing") },
        { name: "Rate Approval", icon: FaCheckCircle, path: withContractId("/OrderManagement/RateApprove") },
        { name: "Risk/Issues", icon: FaExclamationTriangle, path: withContractId("/OrderManagement/RiskAndIssue") },
        { name: "Documentation", icon: FaFileAlt, path: withContractId("/OrderManagement/ProjectEntryDocumentation") },
        { name: "Photos", icon: FaBuilding, path: withContractId("/OrderManagement/PhotoDocumentation") },
        { name: "Reconciliation", icon: FaTasks, path: withContractId("/OrderManagement/Reconciliation") },
        { name: "Do/Don't's", icon: FaWarehouse, path: withContractId("/OrderManagement/Rules") },
        { name: "Vendor Details", icon: FaUsers, path: withContractId("/OrderManagement/ProjectVendorDetails") },
      ],
      [contractId]
    );

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const years = useMemo(() => makeYears(), []);

  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState("");
  const [daysInMonth, setDaysInMonth] = useState(31);

  const [rows, setRows] = useState([]);
  const [backupRows, setBackupRows] = useState([]);

  const dayCols = useMemo(
    () => Array.from({ length: daysInMonth }, (_, i) => i + 1),
    [daysInMonth]
  );

  const clampOutOfMonthToZero = (arr, dim) => {
    return (arr || []).map((r) => {
      const out = { ...emptyDprRow(), ...r };
      for (let d = 1; d <= 31; d++) {
        if (d > dim) {
          out[`p${d}`] = "0";
          out[`a${d}`] = "0";
        } else {
          out[`p${d}`] = toStr(out[`p${d}`] ?? "0");
          out[`a${d}`] = toStr(out[`a${d}`] ?? "0");
        }
      }
      out.remarks = toStr(out.remarks ?? "");
      return out;
    });
  };

  const setCell = (rowIndex, key, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[rowIndex] = { ...(next[rowIndex] || emptyDprRow()), [key]: value };
      return next;
    });
  };

  const loadDPR = async () => {
    if (!contractId) return toast.error("contractId missing in URL");

    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/view?contractId=${contractId}&month=${month}&year=${year}`,
        {
          headers: commonHeaders,
        }
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "View failed");
        setRows([]);
        setBackupRows([]);
        return;
      }

      const dim = toInt(res.data.daysInMonth, 31);
      setDaysInMonth(dim);
      setSource(toStr(res.data.source || ""));

      const normalized = clampOutOfMonthToZero(res.data.rows || [], dim);

      setRows(normalized);
      setBackupRows(JSON.parse(JSON.stringify(normalized)));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ auto-load when contract changes
  useEffect(() => {
    if (contractId) loadDPR();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const savePlanned = async () => {
    if (!contractId) return toast.error("contractId missing in URL");
    if (!userId) return toast.error("login userId missing in localStorage");

    setLoading(true);
    try {
      const payload = {
        contractId,
        month,
        year,
        userId,
        rows: rows.map((r) => {
          const base = {
            categoryId: r.categoryId,
            workId: r.workId,
            unit: r.unit,
            qty: r.qty,
            rate: r.rate,
            amount: r.amount,
            remarks: r.remarks || "",
          };
          for (let d = 1; d <= 31; d++) base[`p${d}`] = toStr(r[`p${d}`] ?? "0");
          return base;
        }),
      };

      const res = await axios.post(`${API}/savePlanned`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Planned Saved Successfully");
        await loadDPR();
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const saveAchieved = async () => {
    if (!contractId) return toast.error("contractId missing in URL");
    if (!userId) return toast.error("login userId missing in localStorage");

    setLoading(true);
    try {
      const payload = {
        contractId,
        month,
        year,
        userId,
        rows: rows.map((r) => {
          const base = { categoryId: r.categoryId, workId: r.workId };
          for (let d = 1; d <= 31; d++) base[`a${d}`] = toStr(r[`a${d}`] ?? "0");
          return base;
        }),
      };

      const res = await axios.post(`${API}/saveAchieved`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Achieved Saved Successfully");
        await loadDPR();
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const undoChanges = () => {
    setRows(JSON.parse(JSON.stringify(backupRows || [])));
    toast.info("Changes reverted");
  };

  // ✅ export with headers from localStorage
  const exportExcel = async () => {
    if (!contractId) return toast.error("contractId missing in URL");

    try {
      setLoading(true);

      const res = await axios.get(
        `${API}/excel?contractId=${contractId}&month=${month}&year=${year}`,
        {
          headers: commonHeaders,
          responseType: "blob",
        }
      );

      const blob = new Blob([res.data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `DPR_MonthWise_${contractId}_${month}_${year}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Excel exported successfully");
    } catch (err) {
      console.error(err);
      toast.error("Excel export failed");
    } finally {
      setLoading(false);
    }
  };

  const doPrint = () => window.print();

  /** ---------------- UI parts ---------------- **/

  const DayCell = ({ rowIndex, day }) => {
    const pKey = `p${day}`;
    const aKey = `a${day}`;

    return (
      <div className="w-12">
        <input
          value={toStr(rows[rowIndex]?.[pKey] ?? "0")}
          onChange={(e) => setCell(rowIndex, pKey, e.target.value)}
          className={`w-12 h-7 text-[11px] px-1 border border-gray-200 outline-none ${
            isDark ? "bg-purple-700 text-white" : "bg-purple-200 text-gray-900"
          }`}
          placeholder="P"
        />
        <input
          value={toStr(rows[rowIndex]?.[aKey] ?? "0")}
          onChange={(e) => setCell(rowIndex, aKey, e.target.value)}
          className={`w-12 h-7 text-[11px] px-1 border border-gray-200 outline-none ${
            isDark ? "bg-blue-700 text-white" : "bg-blue-100 text-gray-900"
          }`}
          placeholder="A"
        />
      </div>
    );
  };

  const ExpandedTimelineView = () => (
    <div className="h-full w-full overflow-auto">
      <table className="min-w-max w-full">
        <thead className="bg-blue-600 text-white sticky top-0 z-10">
          <tr>
            {dayCols.map((d) => (
              <th
                key={d}
                className="px-2 py-3 text-center text-xs font-semibold border-r border-blue-500"
                style={{ minWidth: "60px" }}
              >
                {d}
              </th>
            ))}
            <th
              className="px-4 py-3 text-center text-xs font-semibold"
              style={{ minWidth: "260px" }}
            >
              Remarks / Activity
            </th>
          </tr>
        </thead>

        <tbody className={`${isDark ? "bg-gray-900" : "bg-white"} divide-y divide-gray-200`}>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={dayCols.length + 1} className="px-4 py-10 text-center text-gray-600">
                No Record Found / Enter Rate Approval First
              </td>
            </tr>
          ) : (
            rows.map((r, rowIndex) => (
              <tr
                key={`${r.workId}-${rowIndex}`}
                className={isDark ? "hover:bg-gray-800" : "hover:bg-blue-50"}
              >
                {dayCols.map((d) => (
                  <td key={d} className="border-r border-gray-200 p-0 align-top">
                    <DayCell rowIndex={rowIndex} day={d} />
                  </td>
                ))}

                <td className="px-3 py-2 align-top">
                  <input
                    type="text"
                    value={toStr(rows[rowIndex]?.remarks ?? "")}
                    onChange={(e) => setCell(rowIndex, "remarks", e.target.value)}
                    className={`w-full px-2 py-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      isDark
                        ? "bg-gray-800 border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="Remarks"
                  />

                  <div className={`mt-2 text-[11px] ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    <div className={`font-semibold ${isDark ? "text-gray-100" : "text-gray-800"}`}>
                      {toStr(r.categoryName || "")}
                    </div>
                    <div className="line-clamp-2">{toStr(r.work || "")}</div>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const MainTableView = () => (
    <div className="h-full w-full overflow-auto">
      <table className="min-w-max w-full">
        <thead className="bg-blue-600 text-white sticky top-0 z-10">
          <tr>
            <th className="px-3 py-3 text-left text-xs font-semibold border-r border-blue-500">
              S.No
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold border-r border-blue-500">
              Category
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-semibold border-r border-blue-500"
              style={{ minWidth: "420px" }}
            >
              Ongoing Project Nature Of Work Details
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold border-r border-blue-500">
              Unit
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold border-r border-blue-500">
              Rate
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold border-r border-blue-500">
              QTY
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold border-r border-blue-500">
              Amount
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold border-r border-blue-500">
              Planned / Achieved
            </th>

            {dayCols.map((d) => (
              <th
                key={d}
                className="px-2 py-3 text-center text-xs font-semibold border-r border-blue-500"
                style={{ minWidth: "48px" }}
              >
                {d}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className={`${isDark ? "bg-gray-900" : "bg-white"} divide-y divide-gray-200`}>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={8 + dayCols.length} className="px-4 py-10 text-center text-gray-600">
                No Record Found / Enter Rate Approval First
              </td>
            </tr>
          ) : (
            rows.map((item, index) => (
              <tr
                key={`${item.workId}-${index}`}
                className={
                  isDark
                    ? "hover:bg-gray-800 transition-colors"
                    : "hover:bg-blue-50 transition-colors"
                }
              >
                <td
                  className={`px-3 py-4 text-sm font-medium border-r border-gray-200 ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  {index + 1}
                </td>

                <td className="px-3 py-4 text-sm text-blue-600 border-r border-gray-200">
                  <span className="font-medium">{toStr(item.categoryName)}</span>
                </td>

                <td
                  className={`px-6 py-4 text-sm border-r border-gray-200 ${
                    isDark ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  <div style={{ minWidth: "420px", maxWidth: "520px" }}>
                    <p className="leading-relaxed text-justify">{toStr(item.work)}</p>
                  </div>
                </td>

                <td className="px-3 py-4 text-sm font-medium text-purple-600 border-r border-gray-200">
                  {toStr(item.unit)}
                </td>

                <td className="px-3 py-4 text-sm font-semibold text-green-600 border-r border-gray-200">
                  {toStr(item.rate)}
                </td>

                <td className="px-3 py-4 text-sm text-orange-600 border-r border-gray-200">
                  {toStr(item.qty)}
                </td>

                <td className="px-3 py-4 text-sm font-bold text-green-700 border-r border-gray-200">
                  {toStr(item.amount)}
                </td>

                <td className="px-3 py-4 text-center border-r border-gray-200">
                  <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                    Planned
                  </span>
                  <div className="mt-1">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                      Achieved
                    </span>
                  </div>

                  <input
                    value={toStr(item.remarks ?? "")}
                    onChange={(e) => setCell(index, "remarks", e.target.value)}
                    className={`mt-2 w-full text-[11px] px-2 py-1 border rounded outline-none ${
                      isDark
                        ? "bg-gray-800 border-gray-700 text-white"
                        : "bg-white border-gray-200 text-gray-900"
                    }`}
                    placeholder="Remarks"
                  />
                </td>

                {dayCols.map((d) => (
                  <td key={d} className="border-r border-gray-200 p-0 align-top">
                    <div className="flex flex-col">
                      <input
                        value={toStr(item[`p${d}`] ?? "0")}
                        onChange={(e) => setCell(index, `p${d}`, e.target.value)}
                        className={`w-12 h-7 text-[11px] px-1 border border-gray-200 outline-none ${
                          isDark ? "bg-purple-700 text-white" : "bg-purple-200 text-gray-900"
                        }`}
                        placeholder="P"
                      />
                      <input
                        value={toStr(item[`a${d}`] ?? "0")}
                        onChange={(e) => setCell(index, `a${d}`, e.target.value)}
                        className={`w-12 h-7 text-[11px] px-1 border border-gray-200 outline-none ${
                          isDark ? "bg-blue-700 text-white" : "bg-blue-100 text-gray-900"
                        }`}
                        placeholder="A"
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div
      className={`h-screen overflow-hidden transition-all duration-300 ${
        isDark ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      <ToastContainer position="top-right" autoClose={2500} />

      {/* Top Bar */}
      <div
        className={`h-16 sticky top-0 z-50 backdrop-blur-md border-b ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } shadow-lg`}
      >
        <div className="h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-600"
              }`}
            >
              <FaBars size={20} />
            </button>

            <div className="flex items-center gap-3">
              <FaHome className="text-blue-600 text-xl" />
              <h1 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Project Management System
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div
              className={`text-xs px-3 py-1 rounded-full ${
                isDark ? "bg-gray-700 text-gray-200" : "bg-gray-100 text-gray-700"
              }`}
            >
              Contract: <b>{contractId || "-"}</b>
              {source ? <span className="ml-2 opacity-80">({source})</span> : null}
            </div>

            <button
              onClick={() => setIsDark(!isDark)}
              className={`p-2 rounded-lg transition-colors ${
                isDark
                  ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                  : "bg-gray-800 hover:bg-gray-900 text-white"
              }`}
            >
              {isDark ? "☀️" : "🌙"}
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex h-[calc(100vh-64px)] overflow-hidden">
        {/* Sidebar */}
        <div
          className={`${sidebarOpen ? "w-64" : "w-16"} transition-all duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          } border-r h-full overflow-auto`}
        >
          <div className="p-4">
            {sidebarItems.map((item, index) => (
              <Link
                to={item.path}
                key={index}
                className={`flex items-center gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                  window.location.pathname === item.path
                    ? isDark
                      ? "bg-blue-600 text-white"
                      : "bg-blue-500 text-white"
                    : isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <item.icon size={18} />
                {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
              </Link>
            ))}
          </div>
        </div>

        {/* Main */}
        <div className="flex-1 h-full overflow-hidden">
          <div
            className={`h-full flex flex-col ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            {/* Header */}
            <div
              className={`border-b ${
                isDark ? "bg-gray-900/80 border-gray-700" : "bg-white/80 border-gray-200"
              }`}
            >
              <div className="px-6 py-4">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                  <div>
                    <h1
                      className={`text-2xl lg:text-3xl font-bold flex items-center gap-3 ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      <FaChartBar className="text-blue-500" />
                      PROGRAM/DPR
                    </h1>
                    <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Month-wise DPR Entry (Planned + Achieved)
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <label
                        className={`text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Month:
                      </label>
                      <select
                        value={month}
                        onChange={(e) => setMonth(toInt(e.target.value))}
                        className={`px-3 py-2 border rounded-lg text-sm ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        {monthNames.map((m, idx) => (
                          <option key={m} value={idx + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <label
                        className={`text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Year:
                      </label>
                      <select
                        value={year}
                        onChange={(e) => setYear(toInt(e.target.value))}
                        className={`px-3 py-2 border rounded-lg text-sm ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        {years.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      onClick={loadDPR}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200"
                    >
                      <FaSearch />
                      Search
                    </button>

                    <button
                      onClick={() => setExpandedView(!expandedView)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200"
                    >
                      {expandedView ? <FaCompress /> : <FaExpand />}
                      {expandedView ? "Collapse" : "Expand"}
                    </button>

                    <button
                      onClick={exportExcel}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200"
                    >
                      <FaDownload />
                      Export
                    </button>

                    <button
                      onClick={doPrint}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200"
                    >
                      <FaPrint />
                      Print
                    </button>

                    <button
                      onClick={savePlanned}
                      disabled={loading || rows.length === 0}
                      className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200"
                      title="Saves Planned + Remarks"
                    >
                      <FaSave />
                      Save Planned
                    </button>

                    <button
                      onClick={saveAchieved}
                      disabled={loading || rows.length === 0}
                      className="px-4 py-2 bg-slate-900 hover:bg-black disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200"
                      title="Saves Achieved"
                    >
                      <FaSave />
                      Save Achieved
                    </button>

                    <button
                      onClick={undoChanges}
                      disabled={loading || rows.length === 0}
                      className="px-4 py-2 bg-gray-500 hover:bg-gray-600 disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200"
                    >
                      <FaUndo />
                      Undo
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-6 overflow-hidden">
              <div
                className={`h-full rounded-2xl shadow-2xl border overflow-hidden ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                {expandedView ? <ExpandedTimelineView /> : <MainTableView />}
              </div>

              {loading ? (
                <div
                  className={`mt-3 text-sm flex items-center gap-2 ${
                    isDark ? "text-gray-200" : "text-gray-600"
                  }`}
                >
                  <div className="w-3 h-3 rounded-full bg-blue-600 animate-pulse"></div>
                  Working...
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}